interface CollectionNode<T> {
  uuid: string;
  name: string;
  parentUuid?: string | null;
  children?: T[];
}

/** 返回收藏夹及其所有后代的数量总和。缺少数量字段时保持 undefined。 */
export const getCollectionAggregateCount = <
  T extends CollectionNode<T>,
  K extends keyof T,
>(
  collections: T[],
  uuid: string,
  countField: K,
): number | undefined => {
  const root = buildCollectionTree(collections).flatMap((node) => {
    const visit = (current: T): T | undefined => {
      if (current.uuid === uuid) return current;
      for (const child of current.children || []) {
        const found = visit(child);
        if (found) return found;
      }
      return undefined;
    };
    return [visit(node)].filter((node): node is T => Boolean(node));
  })[0];

  if (!root) return undefined;

  let hasCount = false;
  const sum = (node: T): number => {
    const value = node[countField];
    if (typeof value === 'number') {
      hasCount = true;
    }
    return (
      (typeof value === 'number' ? value : 0) +
      (node.children || []).reduce((total, child) => total + sum(child), 0)
    );
  };

  const total = sum(root);
  return hasCount ? total : undefined;
};

/** 保留旧收藏夹的 UUID；缺失上级或异常父链的节点仍在根层级显示。 */
export const buildCollectionTree = <T extends CollectionNode<T>>(
  collections: T[],
): T[] => {
  const nodes: T[] = collections.map((collection) => ({
    ...collection,
    children: [],
  }));
  const byUuid = new Map(nodes.map((node) => [node.uuid, node]));
  const roots: T[] = [];

  const createsCycle = (node: T, parent: T) => {
    const visited = new Set<string>([node.uuid]);
    let current: T | undefined = parent;
    while (current) {
      if (visited.has(current.uuid)) return true;
      visited.add(current.uuid);
      current = current.parentUuid ? byUuid.get(current.parentUuid) : undefined;
    }
    return false;
  };

  nodes.forEach((node) => {
    const parent = node.parentUuid ? byUuid.get(node.parentUuid) : undefined;
    if (!parent || createsCycle(node, parent)) {
      roots.push(node);
    } else {
      parent.children?.push(node);
    }
  });

  return roots;
};

/** 获取后代 UUID，用于阻止把节点移动到自己的下级。 */
export const getCollectionDescendantUuids = <T extends CollectionNode<T>>(
  collections: T[],
  rootUuid?: string,
): Set<string> => {
  const descendants = new Set<string>();
  if (!rootUuid) return descendants;

  const byUuid = new Map(
    collections.map((collection) => [collection.uuid, collection]),
  );
  collections.forEach((collection) => {
    let parentUuid = collection.parentUuid || null;
    const visited = new Set<string>();
    while (parentUuid && !visited.has(parentUuid)) {
      if (parentUuid === rootUuid) {
        descendants.add(collection.uuid);
        break;
      }
      visited.add(parentUuid);
      parentUuid = byUuid.get(parentUuid)?.parentUuid || null;
    }
  });

  return descendants;
};

/** 展开树并保留深度，让侧栏默认显示所有层级。 */
export const flattenCollectionTree = <T extends CollectionNode<T>>(
  collections: T[],
): Array<T & { depth: number; path: string[] }> => {
  const result: Array<T & { depth: number; path: string[] }> = [];
  const visit = (nodes: T[], depth: number, parentPath: string[]) => {
    nodes.forEach((node) => {
      const path = [...parentPath, node.name];
      result.push({ ...node, depth, path });
      if (node.children?.length) visit(node.children, depth + 1, path);
    });
  };
  visit(buildCollectionTree(collections), 0, []);
  return result;
};
